// React components
import * as React from "react";
import _ from 'lodash';

// internal library
import { isDefined, IsNullOrEmpty, isStringEmpty } from '@Utils/Micro';
import { IComponentSizeProp } from '@Primitive/Interfaces';
import { SearchBar } from '@Primitive/SearchBar';
import { LazyLoadingAnimation } from '@Primitive/Lazy';

const YodiwoSVGBase = React.lazy(() => import("./SvgAsync"));
// ================================================================================================
// ------------------------------------------------------------------------------------------------
export interface SVGIconSelectorProps extends IComponentSizeProp {
    style?: React.CSSProperties;
    packages: string[];
    defaultIcon: string;
    onIconSelection?: (svgPath: string) => void;
}
// ------------------------------------------------------------------------------------------------
export interface SVGIconSelectorState {
    selectedPackage: string;
    selectedIcon: string;
    filteredIconList: { [key: string]: string };
    flags: { [key: string]: string };
}
// ------------------------------------------------------------------------------------------------
export interface YodiwoSVGProps {
    uri: string;
    width: number;
    height: number;
}
// ================================================================================================
export const YodiwoSVG = (props: YodiwoSVGProps) => {
    return <React.Suspense fallback={<LazyLoadingAnimation />}>
        <YodiwoSVGBase {...props} />
    </React.Suspense>
}
// ------------------------------------------------------------------------------------------------
function createSvgUri(pack: string, name: string) {
    return "svg/" + pack + "/" + name;
}
// ------------------------------------------------------------------------------------------------
export function CheckSvgUri(uri: string) {
    if (IsNullOrEmpty(uri))
        return false;
    if (!uri.startsWith("svg/"))
        return false;
    return true;
}
// ------------------------------------------------------------------------------------------------
export function parseSvgUri(uri: string) {
    if (!CheckSvgUri(uri))
        return null;
    let p = uri.replace("svg/", "").split("/");
    if (p.length == 2) {
        return {
            package: p[0],
            name: p[1]
        };
    }
    return null;
}
// ------------------------------------------------------------------------------------------------
export class SVGIconSelector extends React.Component<SVGIconSelectorProps, SVGIconSelectorState> {
    private _searchBarSource: { key: string, value: string }[] = [];
    public static defaultProps: SVGIconSelectorProps = {
        height: 400,
        width: 1024,
        packages: ["Flags"],
        defaultIcon: ""
    };
    // --------------------------------------------------------------------------------------------
    constructor(props: SVGIconSelectorProps) {
        super(props);

        // find the selected icon
        let parse = parseSvgUri(this.props.defaultIcon);

        // clean parse if the selected packages are not contain the package
        if (parse && !_.includes(this.props.packages, parse.package))
            parse = null;

        this.state = {
            selectedPackage: (parse) ? parse.package : this.props.packages[0],
            selectedIcon: (parse) ? parse.name : "",
            filteredIconList: {},
            flags: {}
        };
    }
    componentDidMount() {
        import("./Flags/index")
            .then(Flags => {
                this._searchBarSource = _.map(Flags.default, (icon, key) => {
                    return { key: key.toString(), value: key };
                });
                this.setState({ filteredIconList: Flags.default, flags: Flags.default });
            });
    }
    // --------------------------------------------------------------------------------------------
    onIconSelected(name: string) {
        this.setState({ selectedIcon: name });
        if (isDefined(this.props.onIconSelection))
            this.props.onIconSelection(createSvgUri(this.state.selectedPackage, name));
    }
    // --------------------------------------------------------------------------------------------
    public render() {
        const { width, height, onIconSelection } = this.props;
        const { filteredIconList, selectedIcon } = this.state;
        let colPixel = 65;
        let margin = 4;
        let tileWidth = colPixel + 2 * margin;

        let grid = <div
            key={"row-grid"}
            style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill,minmax(" + tileWidth + "px, 1fr))",
                width: width - 15,
                clear: "both",
                paddingTop: 20
            }}
        >
            {
                _.map(filteredIconList, (d, key) => {
                    return <img
                        width={colPixel}
                        height={colPixel}
                        key={key}
                        style={{ margin: margin }}
                        src={d}
                        onClick={() => this.onIconSelected(key)}
                    >
                    </img>
                })
            }
        </div>;

        const paddingTop = 20;
        const searchBarHeight = 48;
        const selectedColorHeight = 40;

        return <div style={{
            width: width,
            height: this.props.height,
            paddingTop: paddingTop
        }}>
            {/* Search bar */}

            <SearchBar
                simple={true}
                key={'dashboard-search-bar'}
                style={{
                    height: searchBarHeight,
                    maxWidth: width / 2,
                    margin: "0 auto"
                }}
                iconButtonStyle={{ marginLeft: 0, marginRight: 0, marginTop: 0, marginBottom: 0 }}
                source={this._searchBarSource}
                onSearch={(keys, emptySearchValue) => {
                    if (emptySearchValue) {
                        this.setState({ filteredIconList: this.state.flags });
                    } else {
                        this.setState({ filteredIconList: _.pick(this.state.flags, keys) });
                    }
                }}
            />

            {/* Toolbar with select color class name */}
            <div
                key='root-div'
                style={{
                    height: selectedColorHeight,
                    width: width / 2,
                    margin: "0 auto",
                    paddingTop: 14,
                    display: "flex",
                    justifyContent: "space-between"
                }}
            >
                <span
                    key='selected-icon'
                    style={{
                        color: "#9E9E9E",
                        fontSize: 16
                    }}
                >
                    {"Selected Icon :   "}
                </span>
                {!isStringEmpty(selectedIcon)
                    ? <div style={{
                        display: "flex",
                        alignItems: "center",
                        alignContent: "center",
                        justifyContent: "space-between",
                        flex: 1,
                    }}>
                        <img
                            key='icon'
                            width={30}
                            height={30}
                            src={this.state.flags[selectedIcon]}
                            style={{
                                verticalAlign: 'text-bottom',
                                marginLeft: 4
                            }}
                        />

                        <span
                            key='selected-icon-text'
                            style={{
                                color: "#9E9E9E",
                                fontSize: 14,
                                paddingLeft: 10,
                                flex: 1
                            }}
                        >
                            {"  " + selectedIcon}
                        </span>
                        <div
                            key='clear-icon'
                            className="fas fa-times"
                            style={{
                                float: 'right',
                                cursor: 'pointer',
                                color: "#9E9E9E"
                            }}
                            onClick={() => {
                                this.setState({ selectedIcon: null })
                                if (isDefined(onIconSelection))
                                    onIconSelection('');
                            }}
                        />
                    </div>
                    : null}
            </div>

            {/* Filtered flag list*/}
            <div
                key='main-div'
                style={{
                    width: width,
                    height: height - paddingTop - selectedColorHeight - searchBarHeight,
                    overflowX: 'hidden',
                    overflowY: 'auto',
                    paddingBottom: 1
                }}
            >
                {grid}
            </div>
        </div>
    }
    // --------------------------------------------------------------------------------------------
}
// ================================================================================================
